/**
 * Corporate Action Edit Modal Component
 *
 * Modal for viewing, editing, and deleting a corporate action.
 * Only the description field can be edited. Other fields are read-only.
 */

import React, { useMemo, useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Modal,
  TextInput,
} from 'react-native';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import type { SplitConsolidateResponse } from '../../services/tradesApi';

interface CorporateActionEditModalProps {
  visible: boolean;
  corporateAction: SplitConsolidateResponse | null;
  onClose: () => void;
  onSave: (caId: number, description: string) => Promise<void>;
  onDelete: (ca: SplitConsolidateResponse) => void;
  loading?: boolean;
}

export function CorporateActionEditModal({
  visible,
  corporateAction,
  onClose,
  onSave,
  onDelete,
  loading = false,
}: CorporateActionEditModalProps) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);

  // State for editable description
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize description when corporate action changes
  useEffect(() => {
    if (corporateAction) {
      setDescription(corporateAction.description || '');
      setHasChanges(false);
    }
  }, [corporateAction]);

  if (!corporateAction) return null;

  const actionTypeLabels: Record<string, string> = {
    split: t('portfolio.shareSplit', { defaultValue: 'Share Split' }),
    consolidate: t('portfolio.consolidation', { defaultValue: 'Share Consolidation' }),
    bonus: t('portfolio.bonusShares', { defaultValue: 'Bonus Shares' }),
  };

  const actionType = corporateAction.action_type;
  const actionLabel = actionTypeLabels[actionType] || actionType;

  // Format the exclude date for display
  const excludeDate = corporateAction.exclude_date
    ? corporateAction.exclude_date.split('T')[0]
    : '';

  const handleDescriptionChange = (text: string) => {
    setDescription(text);
    setHasChanges(text !== (corporateAction.description || ''));
  };

  const handleSave = async () => {
    if (!hasChanges || saving) return;
    setSaving(true);
    try {
      await onSave(corporateAction.id, description);
      setHasChanges(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal visible={visible} animationType="slide" transparent={true} onRequestClose={onClose}>
      <View style={styles.modalOverlay}>
        <View style={[styles.modalContent, { backgroundColor: theme.colors.surface }]}>
          {/* Header */}
          <View style={[styles.header, { borderBottomColor: theme.colors.border }]}>
            <Text style={[styles.title, { color: theme.colors.text }]}>
              {t('portfolio.editCorporateAction', { defaultValue: 'Edit Corporate Action' })}
            </Text>
            <TouchableOpacity
              onPress={onClose}
              style={styles.closeButton}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            >
              <Text style={[styles.closeButtonText, { color: theme.colors.textSecondary }]}>✕</Text>
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.scrollView}>
            {/* Corporate Action Type (Read-only) */}
            <View style={styles.fieldContainer}>
              <Text style={[styles.label, { color: theme.colors.text }]}>
                {t('portfolio.actionType', { defaultValue: 'Action Type' })}
              </Text>
              <View
                style={[
                  styles.readOnlyField,
                  {
                    backgroundColor: theme.colors.backgroundElevated,
                    borderColor: theme.colors.border,
                  },
                ]}
              >
                <Text style={[styles.readOnlyText, { color: theme.colors.text }]}>
                  {actionLabel}
                </Text>
              </View>
            </View>

            {/* Symbol (Read-only) */}
            <View style={styles.fieldContainer}>
              <Text style={[styles.label, { color: theme.colors.text }]}>
                {t('portfolio.symbol', { defaultValue: 'Symbol' })}
              </Text>
              <View
                style={[
                  styles.readOnlyField,
                  {
                    backgroundColor: theme.colors.backgroundElevated,
                    borderColor: theme.colors.border,
                  },
                ]}
              >
                <Text style={[styles.readOnlyText, { color: theme.colors.text }]}>
                  {corporateAction.symbol}
                </Text>
              </View>
            </View>

            {/* Exclude Date (Read-only) */}
            <View style={styles.fieldContainer}>
              <Text style={[styles.label, { color: theme.colors.text }]}>
                {t('portfolio.excludeDate', { defaultValue: 'Effective Date' })}
              </Text>
              <View
                style={[
                  styles.readOnlyField,
                  {
                    backgroundColor: theme.colors.backgroundElevated,
                    borderColor: theme.colors.border,
                  },
                ]}
              >
                <Text style={[styles.readOnlyText, { color: theme.colors.text }]}>
                  {excludeDate}
                </Text>
              </View>
              <Text style={[styles.helperText, { color: theme.colors.textTertiary }]}>
                {t('portfolio.excludeDateHelp', {
                  defaultValue: 'Trades ON or AFTER this date are NOT affected',
                })}
              </Text>
            </View>

            {/* Ratio Details */}
            <View style={styles.fieldContainer}>
              <Text style={[styles.label, { color: theme.colors.text }]}>
                {t('portfolio.ratioDetails', { defaultValue: 'Ratio Details' })}
              </Text>
              <View
                style={[
                  styles.readOnlyField,
                  {
                    backgroundColor: theme.colors.backgroundElevated,
                    borderColor: theme.colors.border,
                  },
                ]}
              >
                {actionType === 'bonus' ? (
                  <Text style={[styles.readOnlyText, { color: theme.colors.text }]}>
                    {t('portfolio.bonusRatio', {
                      defaultValue: '{{bonus}} bonus for every {{every}} shares',
                      bonus: corporateAction.to_shares,
                      every: corporateAction.from_shares,
                    })}
                  </Text>
                ) : actionType === 'split' ? (
                  <Text style={[styles.readOnlyText, { color: theme.colors.text }]}>
                    {t('portfolio.splitRatioDisplay', {
                      defaultValue: '{{to}} for every {{from}} shares',
                      to: corporateAction.to_shares,
                      from: corporateAction.from_shares,
                    })}
                  </Text>
                ) : (
                  <Text style={[styles.readOnlyText, { color: theme.colors.text }]}>
                    {t('portfolio.consolidateRatioDisplay', {
                      defaultValue: '{{from}} consolidated to {{to}}',
                      from: corporateAction.from_shares,
                      to: corporateAction.to_shares,
                    })}
                  </Text>
                )}
              </View>
            </View>

            {/* Description (Editable) */}
            <View style={styles.fieldContainer}>
              <Text style={[styles.label, { color: theme.colors.text }]}>
                {t('portfolio.description', { defaultValue: 'Description' })}
              </Text>
              <TextInput
                style={[
                  styles.descriptionInput,
                  {
                    backgroundColor: theme.colors.backgroundElevated,
                    borderColor: theme.colors.border,
                    color: theme.colors.text,
                  },
                ]}
                value={description}
                onChangeText={handleDescriptionChange}
                placeholder={t('portfolio.descriptionPlaceholder', {
                  defaultValue: 'Add a description (optional)',
                })}
                placeholderTextColor={theme.colors.textTertiary}
                multiline
                numberOfLines={3}
                textAlignVertical="top"
                maxLength={500}
                editable={!saving}
              />
              <Text style={[styles.charCount, { color: theme.colors.textTertiary }]}>
                {description.length}/500
              </Text>
            </View>

            {/* Warning Text */}
            <View
              style={[
                styles.warningBox,
                { backgroundColor: theme.colors.warning + '10', borderColor: theme.colors.warning },
              ]}
            >
              <Text style={[styles.warningTitle, { color: theme.colors.warning }]}>
                {t('portfolio.caEditWarningTitle', { defaultValue: 'Limited Editing' })}
              </Text>
              <Text style={[styles.warningText, { color: theme.colors.textSecondary }]}>
                {t('portfolio.caEditWarningText', {
                  defaultValue:
                    'Only the description can be changed. To modify the ratio or other details, delete this corporate action and create a new one.',
                })}
              </Text>
            </View>
          </ScrollView>

          {/* Footer Buttons */}
          <View style={[styles.footer, { borderTopColor: theme.colors.border }]}>
            {/* Cancel Button */}
            <TouchableOpacity
              style={[
                styles.footerButton,
                styles.cancelButton,
                {
                  backgroundColor: theme.colors.backgroundElevated,
                  borderWidth: 1,
                  borderColor: theme.colors.border,
                },
              ]}
              onPress={onClose}
              disabled={loading || saving}
            >
              <Text style={[styles.footerButtonText, { color: theme.colors.textSecondary }]}>
                {t('common.cancel')}
              </Text>
            </TouchableOpacity>

            {/* Save Button - Only enabled when there are changes */}
            {hasChanges && (
              <TouchableOpacity
                style={[
                  styles.footerButton,
                  styles.saveButton,
                  { backgroundColor: saving ? theme.colors.border : theme.colors.primary },
                ]}
                onPress={handleSave}
                disabled={saving || loading}
              >
                <Text
                  style={[
                    styles.footerButtonText,
                    saving && { color: theme.colors.textTertiary },
                    styles.saveButtonText,
                  ]}
                >
                  {saving ? t('common.saving', { defaultValue: 'Saving...' }) : t('common.save')}
                </Text>
              </TouchableOpacity>
            )}

            {/* Delete Button */}
            <TouchableOpacity
              style={[
                styles.footerButton,
                styles.deleteButton,
                { backgroundColor: loading ? theme.colors.border : theme.colors.error },
              ]}
              onPress={() => onDelete(corporateAction)}
              disabled={loading || saving}
            >
              <Text
                style={[
                  styles.footerButtonText,
                  (loading || saving) && { color: theme.colors.textTertiary },
                  styles.deleteButtonText,
                ]}
              >
                {loading
                  ? t('common.deleting', { defaultValue: 'Deleting...' })
                  : t('common.delete')}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

function createThemedStyles(theme: any) {
  return StyleSheet.create({
    modalOverlay: {
      flex: 1,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      justifyContent: 'flex-end',
    },
    modalContent: {
      borderTopLeftRadius: theme.radii.xl,
      borderTopRightRadius: theme.radii.xl,
      maxHeight: '85%',
      borderWidth: theme.id === 'bauhaus' ? 2 : 0,
      borderColor: theme.colors.border,
    },
    header: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: 16,
      borderBottomWidth: theme.id === 'bauhaus' ? 2 : 1,
    },
    title: {
      fontSize: theme.typography.fontSizeLg,
      fontWeight: theme.typography.fontWeightBold,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
    closeButton: {
      padding: 8,
    },
    closeButtonText: {
      fontSize: 24,
      fontWeight: '300',
    },
    scrollView: {
      padding: 16,
      maxHeight: '60%',
    },
    fieldContainer: {
      marginBottom: 16,
    },
    label: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
      marginBottom: 8,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
      letterSpacing: theme.id === 'bauhaus' ? 1 : 0,
    },
    readOnlyField: {
      padding: 12,
      borderRadius: theme.radii.md,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
    },
    readOnlyText: {
      fontSize: theme.typography.fontSizeMd,
    },
    descriptionInput: {
      padding: 12,
      borderRadius: theme.radii.md,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      fontSize: theme.typography.fontSizeMd,
      minHeight: 80,
      maxHeight: 120,
    },
    charCount: {
      fontSize: theme.typography.fontSizeXs,
      marginTop: 4,
      textAlign: 'right',
    },
    helperText: {
      fontSize: theme.typography.fontSizeXs,
      marginTop: 4,
      fontStyle: 'italic',
    },
    warningBox: {
      padding: 12,
      borderRadius: theme.radii.md,
      borderWidth: 1,
      marginTop: 8,
    },
    warningTitle: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
      marginBottom: 4,
    },
    warningText: {
      fontSize: theme.typography.fontSizeSm,
      lineHeight: 18,
    },
    footer: {
      flexDirection: 'row',
      padding: 16,
      gap: 12,
      borderTopWidth: theme.id === 'bauhaus' ? 2 : 1,
    },
    footerButton: {
      flex: 1,
      paddingVertical: 14,
      paddingHorizontal: 24,
      borderRadius: theme.radii.md,
      alignItems: 'center',
      minHeight: 48,
      justifyContent: 'center',
    },
    footerButtonText: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
    cancelButton: {
      // Border already set in inline style
    },
    saveButton: {
      // Background color set in inline style
    },
    saveButtonText: {
      color: theme.colors.textInverse,
    },
    deleteButton: {
      // Background color set in inline style
    },
    deleteButtonText: {
      color: theme.colors.textInverse,
    },
  });
}
